import { Injectable } from "@angular/core";
import Fuse from "fuse.js";
import { find, values } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { GamingExternalState } from "../state";

import { Game, GameDetail, GameState } from "./game.model";

@Injectable({
	providedIn: "root",
})
export class GameSelector {
	getAll() {
		return (state: GamingExternalState): Record<string, Game> => state.gamingExternal.game.games;
	}

	getState() {
		return (state: GamingExternalState): GameState => state.gamingExternal.game;
	}

	getById(id: string) {
		return (state: GamingExternalState): Game | undefined => state.gamingExternal.game.games[id];
	}

	getBySlug(slug: string) {
		return (state: GamingExternalState): Game | undefined =>
			this.getById(state.gamingExternal.game.slugs?.[slug])(state);
	}

	getByPrimaryVariantId(primaryVariantId: string) {
		return (state: GamingExternalState): Game | undefined =>
			find(state.gamingExternal.game.games, (game) => game.primaryVariantId === primaryVariantId);
	}

	getDetailById(id: string) {
		return (state: GamingExternalState): GameDetail | undefined => state.gamingExternal.game.gameDetails[id];
	}

	search(query: string) {
		return (state: GamingExternalState): Game[] => {
			const searchKeys: (keyof Game)[] = ["name", "gameStudio", "translation"];

			const options: Fuse.FuseOptions<Game> = {
				caseSensitive: false,
				shouldSort: true,
				threshold: 0.3,
				location: 0,
				distance: 100,
				maxPatternLength: 20,
				keys: searchKeys,
			};

			const games = values(state.gamingExternal.game.games);
			const fuse = new Fuse(games, options);

			return fuse.search(query) as Game[];
		};
	}

	search$(query: string) {
		return (state$: Observable<GamingExternalState>): Observable<Game[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.gamingExternal.game.games),
				map(this.search(query))
			);
	}

	isBusy() {
		return (state: GamingExternalState): boolean =>
			!state.gamingExternal.game.initialGamesFetched && !state.gamingExternal.game.allGamesFetched;
	}

	isAllGamesFetched() {
		return (state: GamingExternalState): boolean => state.gamingExternal.game.allGamesFetched;
	}

	isGameFetched(gameId: string) {
		return (state: GamingExternalState): boolean => !!state.gamingExternal.game.gameDetails[gameId];
	}

	getGamesByGameIds(gameIds: string[]) {
		return (state: GamingExternalState): Game[] =>
			gameIds
				.filter((gameId) => !!state.gamingExternal.game?.games?.[gameId])
				.map((gameId) => state.gamingExternal.game?.games?.[gameId]);
	}
}
